import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { CONFIG_BUILD_ENV } from '@src/config/env'
import { QuinceRouter } from '@src/modules/navigation'
import {
  EFilterKeys,
  Filter,
  FilterOptions,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import { PRODUCT_FEED_TYPE } from '@src/quince-site-common/src/services/productFeedService/productFeedService.types'
import { getKebabCaseURLFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { pickBy } from '@src/utils/functions/utils'

import {
  ALL_SUBCOLLECTION_FILTER_ID,
  PFY_DYNAMIC_COLLECTION_ID_QA,
  PFY_DYNAMIC_COLLECTION_ID_PROD,
} from './collection.constants'
import Collection, { SubCollection } from './domains/Collection'

export function isCustomSection(section: CustomSection | SubCollection): section is CustomSection {
  return section.typename === 'CustomSection'
}

export function shouldShowBorder(currentIndex: number, sections: (SubCollection | CustomSection)[]) {
  const nextSection = sections[currentIndex + 1]
  const nextIsCustomSection = nextSection && isCustomSection(nextSection)
  const nextIsFaqSection = nextIsCustomSection && nextSection.sectionType === CUSTOM_SECTION_TYPE.ACCORDION
  const isLastSection = currentIndex === sections.length - 1
  const showBorderBottom = !nextIsFaqSection && !isLastSection
  return showBorderBottom
}

export const getSubCollections = (subCollections: (SubCollection | CustomSection)[]) => {
  // get only subcollections
  return subCollections.filter(sc => sc.typename !== 'CustomSection')
}

export const shouldShowSubCollectionCards = (subCollections: SubCollection[] = []) => {
  if (subCollections.length <= 1) {
    return false
  }
  const subCollectionWithCardData = subCollections.filter(sc => sc.imageDesktop !== undefined)
  return subCollections.length === subCollectionWithCardData.length
}

export const shouldEnrichCustomSection = (customSection: CustomSection) =>
  (customSection.sectionType === CUSTOM_SECTION_TYPE.PRODUCT_LIST_TWO ||
    customSection.sectionType === CUSTOM_SECTION_TYPE.PRODUCT_LIST_THREE) &&
  (customSection.productFeedType == PRODUCT_FEED_TYPE.CUSTOMER_TOP_SIMILAR_PRODUCTS ||
    customSection.productFeedType == PRODUCT_FEED_TYPE.FBT)

export const getEnrichedCustomSection = (
  customSection: CustomSection,
  subCollections: (SubCollection | CustomSection)[],
): CustomSection => {
  return {
    ...customSection,
    productFeedType: PRODUCT_FEED_TYPE.FBT,
    productFeedMetaData: {
      productCards: subCollections
        .filter(s => s.typename !== 'CustomSection')
        .map(s => s)
        .map(s => s.productCards)
        .flat(),
    },
  }
}

export const getFormattedSlug = (slug: string) => {
  let finalSlug = slug
  if (slug.startsWith('/')) {
    finalSlug = slug.slice(1)
  }
  return finalSlug
}

const ALL_SUBCOLLECTION_IDS = [ALL_SUBCOLLECTION_FILTER_ID, 'All', 'all'] as const
const COLLECTION_PRODUCT_SCROLL_KEY = 'collection:pending-product-scroll'

interface SubcollectionCardLike {
  id?: string | number | null
  title?: string | null
  slug?: string | null
}

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '')

const normalizeTitle = (value: string) => value.trim().toLowerCase()

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function isAllSubcollectionValue(value?: string | null): boolean {
  if (!value) return false
  return ALL_SUBCOLLECTION_IDS.some(candidate => candidate.toLowerCase() === value.toLowerCase())
}

export function normalizeCollectionPath(pathOrSlug: string): string {
  const trimmedPath = trimSlashes(pathOrSlug)
  return trimmedPath ? `/${trimmedPath}` : '/'
}

export function buildSubcollectionPath({
  collectionSlug,
  subcollectionSlug,
  subcollectionId,
  subcollectionTitle,
}: {
  collectionSlug: string
  subcollectionSlug?: string | null
  subcollectionId?: string | number | null
  subcollectionTitle?: string | null
}): string {
  const collectionPath = normalizeCollectionPath(collectionSlug)

  if (
    isAllSubcollectionValue(subcollectionSlug) ||
    isAllSubcollectionValue(subcollectionTitle) ||
    isAllSubcollectionValue(subcollectionId ? String(subcollectionId) : null)
  ) {
    return collectionPath
  }

  if (subcollectionSlug) {
    const normalizedSlug = normalizeCollectionPath(subcollectionSlug)
    if (normalizedSlug === collectionPath || normalizedSlug.startsWith(`${collectionPath}/`)) {
      return normalizedSlug
    }

    const slugSegment = trimSlashes(subcollectionSlug)
    if (slugSegment) {
      return `${collectionPath}/${slugSegment}`
    }
  }

  const slugSource = subcollectionTitle ?? (subcollectionId ? String(subcollectionId) : null)
  if (!slugSource) {
    return collectionPath
  }

  const slugSegment = getKebabCaseURLFromInputString(slugSource)
  return slugSegment ? `${collectionPath}/${slugSegment}` : collectionPath
}

export function findSubcollectionPath({
  collectionSlug,
  subcollectionValue,
  subCollectionCards,
}: {
  collectionSlug: string
  subcollectionValue?: string | null
  subCollectionCards?: SubcollectionCardLike[]
}): string {
  if (!subcollectionValue || isAllSubcollectionValue(subcollectionValue)) {
    return normalizeCollectionPath(collectionSlug)
  }

  const matchedCard = subCollectionCards?.find(card => {
    if (!card.title) return false
    return normalizeTitle(card.title) === normalizeTitle(subcollectionValue)
  })

  return buildSubcollectionPath({
    collectionSlug,
    subcollectionSlug: matchedCard?.slug,
    subcollectionId: matchedCard?.id,
    subcollectionTitle: matchedCard?.title ?? subcollectionValue,
  })
}

export function getPathSelectedSubcollection({
  collectionSlug,
  currentPath,
  subCollectionCards,
}: {
  collectionSlug: string
  currentPath: string
  subCollectionCards?: SubcollectionCardLike[]
}): string | null {
  const normalizedCurrentPath = normalizeCollectionPath(currentPath)
  const normalizedCollectionPath = normalizeCollectionPath(collectionSlug)

  if (normalizedCurrentPath === normalizedCollectionPath) {
    return null
  }

  const matchedCard = subCollectionCards?.find(card => {
    if (!card.slug) return false
    return normalizeCollectionPath(card.slug) === normalizedCurrentPath
  })

  return matchedCard?.title ?? null
}

export function mergePathSelectedSubcollection(
  selectedFilters: SelectedFilter,
  pathSelectedSubcollection?: string | null,
): SelectedFilter {
  if (!pathSelectedSubcollection) {
    return selectedFilters
  }

  const selectedSubcollections = SelectedFilter.getSelectedFilterValuesByFilterName(
    selectedFilters,
    EFilterKeys.SUBCOLLECTIONS,
  )
  if (selectedSubcollections.length > 0) {
    return selectedFilters
  }

  return SelectedFilter.addValueToSelectedFilter(selectedFilters, EFilterKeys.SUBCOLLECTIONS, pathSelectedSubcollection)
}

export function stripPathSelectedSubcollection(
  selectedFilters: SelectedFilter,
  pathSelectedSubcollection?: string | null,
): SelectedFilter {
  if (!pathSelectedSubcollection) {
    return selectedFilters
  }

  const selectedSubcollections = SelectedFilter.getSelectedFilterValuesByFilterName(
    selectedFilters,
    EFilterKeys.SUBCOLLECTIONS,
  )
  if (!selectedSubcollections.includes(pathSelectedSubcollection)) {
    return selectedFilters
  }

  return SelectedFilter.removeValueFromSelectedFilter(
    selectedFilters,
    pathSelectedSubcollection,
    EFilterKeys.SUBCOLLECTIONS,
  )
}

// Builds the search string to append on a path-mode subcollection navigation. Drops the user-
// applied `filter`/`sort`/`sortBy` params (mirroring query-mode's clearAllFilters / setOnlyFilter
// reset on subcollection change) while preserving rollout/experiment/marketing params such as
// `plEnabled`, `utm_*`, and statsig overrides. Returns "" or "?<remaining>".
export function buildSubcollectionNavigationSearch(currentSearch: string): string {
  if (!currentSearch) return ''
  const params = new URLSearchParams(currentSearch)
  /* eslint-disable i18next/no-literal-string */
  params.delete('filter')
  params.delete('sort')
  params.delete('sortBy')
  /* eslint-enable i18next/no-literal-string */
  const result = params.toString()
  return result ? `?${result}` : ''
}

export function setPendingCollectionProductScroll(targetPath: string): void {
  const storage = getSessionStorage()
  if (!storage) {
    return
  }

  storage.setItem(COLLECTION_PRODUCT_SCROLL_KEY, normalizeCollectionPath(targetPath))
}

export function consumePendingCollectionProductScroll(currentPath: string): boolean {
  const storage = getSessionStorage()
  if (!storage) {
    return false
  }

  const expectedPath = storage.getItem(COLLECTION_PRODUCT_SCROLL_KEY)
  if (!expectedPath) {
    return false
  }

  const normalizedCurrentPath = normalizeCollectionPath(currentPath)
  const shouldScroll = expectedPath === normalizedCurrentPath

  if (shouldScroll) {
    storage.removeItem(COLLECTION_PRODUCT_SCROLL_KEY)
  }

  return shouldScroll
}

export const getCollectionCacheKey = (pathname: string, searchParams: string): string => {
  if (!searchParams) return pathname
  return `${pathname}?${searchParams}`
}

export const resolveSubcollectionFilterValue = (title: string, filters: Filter[], isV2Format: boolean): string => {
  if (!isV2Format) return title
  const subFilter = filters.find(f => f.name === (EFilterKeys.SUBCOLLECTIONS as string))
  const match = subFilter?.values.find(
    (v): v is FilterOptions => typeof v !== 'string' && v.nameForSubcollectionPill === title,
  )
  return match?.name ?? title
}

export const resolveSubcollectionDisplayTitle = (filterValue: string, filters: Filter[]): string => {
  const subFilter = filters.find(f => f.name === (EFilterKeys.SUBCOLLECTIONS as string))
  const match = subFilter?.values.find((v): v is FilterOptions => typeof v !== 'string' && v.name === filterValue)
  return match?.nameForSubcollectionPill ?? ''
}

// TODO: Move this to functions/utils.ts
export const isUndefined = (value: unknown) => value === undefined

export const getPFYDynamicCollectionId = () => {
  if (CONFIG_BUILD_ENV === BUILD_ENVIRONMENTS.QA) return PFY_DYNAMIC_COLLECTION_ID_QA
  return PFY_DYNAMIC_COLLECTION_ID_PROD
}

export const getUpdatedSelectedFilters = ({
  filter,
  sort,
  selectedFilters,
  sortBy,
  options,
  collectionPage,
  isNewCLPFiltersExperimentActive,
  router,
}: {
  filter: UpdateQueryParams['filter']
  sort: UpdateQueryParams['sort']
  selectedFilters: SelectedFilter
  sortBy: string
  router: QuinceRouter
  options?: { skipFilterClickEvent?: boolean }
  collectionPage?: Collection
  isNewCLPFiltersExperimentActive?: boolean
}) => {
  let updatedSelectedFilters = selectedFilters
  let updatedSortBy = sortBy

  if (filter) {
    switch (filter.type) {
      case 'remove': {
        updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
          selectedFilters,
          filter.value,
          filter.name,
        )
        trackEvent(TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED, {
          filter_key: filter.name,
          filter_value: filter.value,
        })
        break
      }
      case 'toggle': {
        if (filter.name !== (EFilterKeys.PRODUCT_TYPE as string)) {
          updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
            selectedFilters,
            filter.name,
            filter.value,
          )
        }
        if (filter.name === (EFilterKeys.PRODUCT_TYPE as string) && isNewCLPFiltersExperimentActive) {
          updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
            selectedFilters,
            EFilterKeys.SUBCOLLECTIONS,
            filter.value,
          )
        }
        if (!options?.skipFilterClickEvent) {
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
            label: filter.label,
            value: filter.value,
            context: {
              ...(collectionPage && {
                collectionSlug: collectionPage.slug,
                collectionTitle: collectionPage.title,
              }),
              isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
              selectedFilters: pickBy(updatedSelectedFilters as Record<string, unknown>, v => v !== undefined),
              filter_key: filter.name,
              filter_value: filter.value,
            },
          })
          if (
            !isNewCLPFiltersExperimentActive &&
            SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value)
          ) {
            trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
              filter_key: filter.name,
              filter_value: filter.value,
            })
          }
        }

        // ISSUE: @tech-debt productType shouldn't be a filter and isn't a filter - forced custom logic shouldn't be added
        if (filter.name === (EFilterKeys.PRODUCT_TYPE as string) && !isNewCLPFiltersExperimentActive) {
          const targetPath = `/${getFormattedSlug(filter.value)}`
          setPendingCollectionProductScroll(targetPath)
          router.push(targetPath)
        }
        break
      }

      case 'reset': {
        updatedSelectedFilters = new SelectedFilter({})
        trackEvent(TELEMETRY_EVENT_NAMES.FILTERS_CLEARED, {})
        break
      }

      case 'overwrite': {
        updatedSelectedFilters = filter.updatedFilters
        break
      }

      case 'removeAll': {
        updatedSelectedFilters = SelectedFilter.removeAllValuesFromSelectedFilter(selectedFilters, filter.name)
        if (!options?.skipFilterClickEvent) {
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
            label: filter.label,
            value: filter.value,
            context: {
              ...(collectionPage && {
                collectionSlug: collectionPage.slug,
                collectionTitle: collectionPage.title,
              }),
              isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
              selectedFilters: pickBy(updatedSelectedFilters as Record<string, unknown>, v => v !== undefined),
              filter_key: filter.name,
              filter_value: filter.value,
            },
          })
        }
        break
      }
      default:
        break
    }
  }

  if (sort) {
    switch (sort.type) {
      case 'toggle': {
        const sortValue = sort.value
        updatedSortBy = sortValue === sortBy ? SORT_BY.FEATURED.value : sortValue
        break
      }
      case 'reset': {
        updatedSortBy = SORT_BY.FEATURED.value
        break
      }
      default:
        break
    }

    const isDefaultOption = updatedSortBy === SORT_BY.FEATURED.value
    if (!isDefaultOption) {
      trackEvent(TELEMETRY_EVENT_NAMES.SORT_USED, {
        sort_used: updatedSortBy,
      })
    }
  }

  return { updatedSelectedFilters, updatedSortBy }
}
